import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, GlobeAltIcon, BuildingOffice2Icon, CheckIcon } from './icons';
import type { MasterCrudCompany } from './MasterCrud.types';

interface CompanySelectorProps {
  companies: MasterCrudCompany[];
  selectedCompany?: MasterCrudCompany;
  onCompanyChange: (company: MasterCrudCompany, ...args: any[]) => void;
  className?: string;
  disabled?: boolean;
  /**
   * Cuando es true, la compañía se muestra como etiqueta estática (sin interacción).
   * Se usa cuando companyRequired=true en MasterCrud: no existe habilitación ni cambio
   * de contexto posible, por lo que un comboBox no aplica.
   * Renderiza un <span> (no un <button>) para que no sea posible habilitarlo desde devtools.
   */
  readOnly?: boolean;
  /**
   * Mapa opcional uuid → habilitado. Cuando se provee, cada opción de compañía muestra
   * su estado de habilitación (check verde "Habilitado" / círculo hueco "No habilitado").
   * Usado en la página de detalle multicompañía (sobrescritura).
   */
  companyEnabledMap?: Record<string, boolean>;
  /** Función de traducción inyectada por el padre */
  t: (key: string, defaultValue?: string) => string;
}

/**
 * CompanySelector - Combobox de contexto de compañía para MasterCrud.
 *
 * Diseño alineado al prototipo (azul de marca): trigger `bg-[#eff8ff]` con borde
 * `#93d1fd`, icono Globe/Building2 + ChevronDown; dropdown con opción Global,
 * divisor y compañías (nombre + código), check azul en la opción activa.
 */
export const CompanySelector: React.FC<CompanySelectorProps> = ({
  companies,
  selectedCompany,
  onCompanyChange,
  className = '',
  disabled = false,
  readOnly = false,
  companyEnabledMap,
  t,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!companies.length) return null;

  const isGlobal = !selectedCompany || String(selectedCompany.uuid) === '000';
  const ContextIcon = isGlobal ? GlobeAltIcon : BuildingOffice2Icon;

  const companyLabel = selectedCompany?.name
    || (companies.length > 0 ? t('loading', 'Cargando...') : t('selectCompany', 'Seleccionar Compañía'));

  // Trigger azul de marca (tokens del prototipo → clases del kit con dark:)
  const triggerClasses = [
    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold leading-none select-none transition-colors',
    'bg-[#eff8ff] dark:bg-primary-900/20 border border-[#93d1fd] dark:border-primary-800 text-[#1355b7] dark:text-primary-300',
    disabled
      ? 'opacity-60 cursor-not-allowed'
      : 'cursor-pointer hover:bg-[#dbeefe] dark:hover:bg-primary-900/40 hover:border-[#3c9bf6]',
  ].join(' ');

  const iconClasses = 'w-3.5 h-3.5 flex-shrink-0';

  // ── Modo solo lectura (companyRequired=true en la página de edición) ──────
  // <span> en lugar de <button> para que no exista ningún control interactivo.
  if (readOnly) {
    return (
      <div className={`relative inline-block ${className}`}>
        <span className={triggerClasses.replace('cursor-pointer', '').replace(/hover:[^\s]+/g, '')}>
          <ContextIcon className={iconClasses} aria-hidden="true" />
          <span className="max-w-[180px] truncate" title={companyLabel}>{companyLabel}</span>
        </span>
      </div>
    );
  }

  const renderOption = (company: MasterCrudCompany) => {
    const optIsGlobal = String(company.uuid) === '000';
    const OptIcon = optIsGlobal ? GlobeAltIcon : BuildingOffice2Icon;
    const active = selectedCompany?.uuid === company.uuid || (isGlobal && optIsGlobal);
    const enabled = companyEnabledMap ? companyEnabledMap[String(company.uuid)] : undefined;

    return (
      <button
        key={company.uuid}
        type="button"
        role="menuitem"
        onClick={() => { onCompanyChange(company); setOpen(false); }}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
      >
        <span className="w-3 flex-shrink-0 text-[#0e79fd]">
          {active && <CheckIcon className="w-3 h-3" />}
        </span>
        <span className="flex-shrink-0 text-[#1355b7] dark:text-primary-300">
          <OptIcon className="w-3.5 h-3.5" />
        </span>
        <span className="font-medium truncate min-w-0" title={company.name}>{company.name}</span>
        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
          {!optIsGlobal && company.code != null && (
            <span className="text-content-secondary tabular-nums">{company.code}</span>
          )}
          {!optIsGlobal && enabled !== undefined && (
            enabled ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#4d7c0f]">
                <CheckIcon className="w-3 h-3" />{t('enabled', 'Habilitado')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-content-secondary">
                <span className="inline-block w-3 h-3 rounded-full border border-current" />{t('notEnabled', 'No habilitado')}
              </span>
            )
          )}
          {optIsGlobal && companyEnabledMap && (
            <span className="text-[11px] text-content-secondary">{t('masterRecord', 'Registro maestro')}</span>
          )}
        </span>
      </button>
    );
  };

  const globalCompanies = companies.filter((c) => String(c.uuid) === '000');
  const realCompanies = companies.filter((c) => String(c.uuid) !== '000');
  const wideDropdown = !!companyEnabledMap;

  return (
    <div className={`relative inline-block ${className}`} ref={rootRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={triggerClasses}
      >
        <span className="flex-shrink-0"><ContextIcon className={iconClasses} /></span>
        <span className="max-w-[180px] truncate" title={companyLabel}>{companyLabel}</span>
        <ChevronDownIcon className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute left-0 top-full mt-1 z-50 ${wideDropdown ? 'min-w-[360px] max-w-[440px]' : 'min-w-[280px] max-w-[380px]'} bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-lg shadow-md py-1`}
        >
          {globalCompanies.map(renderOption)}
          {globalCompanies.length > 0 && realCompanies.length > 0 && (
            <hr className="my-1 border-border-primary dark:border-dark-border-primary" />
          )}
          {realCompanies.map(renderOption)}
        </div>
      )}
    </div>
  );
};
